import React, { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import PreorderCountdown from '../components/PreorderCountdown';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Hero from '../components/Hero';
import CategoryFilter from '../components/CategoryFilter';
import DishCard from '../components/DishCard';
import AssistantWidget from '../components/AssistantWidget';
import { useApp } from '../context/AppContext';
import { UtensilsCrossed, CalendarClock, CalendarRange, Salad, CakeSlice, CupSoda, ShoppingBag, X as CloseIcon } from 'lucide-react';

const TABS = [
  { key: 'week', labelKey: 'tab_week', icon: CalendarClock },
  { key: 'entrees', labelKey: 'tab_entrees', icon: Salad },
  { key: 'desserts', labelKey: 'tab_desserts', icon: CakeSlice },
  { key: 'boissons', labelKey: 'tab_boissons', icon: CupSoda },
  { key: 'week2', labelKey: 'tab_week2', icon: CalendarRange },
];

// Monday of the served week (Mon-Fri); on weekends, next Monday.
// Fallback only: the server says which week each tab shows (menuState), so the
// labels follow the Thursday rotation rather than the calendar.
function servedMonday(base = new Date()) {
  const x = new Date(base);
  const day = x.getDay();
  const diff = day === 6 ? 2 : day === 0 ? 1 : 1 - day;
  x.setDate(x.getDate() + diff);
  return x;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function fmt(d) { return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`; }
function isoToLocal(iso) { const [y, m, d] = iso.split('-').map(Number); return new Date(y, m - 1, d); }
function startOfToday() { const x = new Date(); x.setHours(0, 0, 0, 0); return x; }

export default function MenuPage() {
  const { dishes, t, tf, menuState, cartCount, setCartOpen, cartNotice, dismissCartNotice } = useApp();

  // The active tab lives in the URL, not in component state. Someone reading
  // next week's dishes is there to pre-order, and coming back from a dish must
  // return them to next week — not drop them on the current week, which is
  // already in the fridge and cannot be ordered.
  const [params, setParams] = useSearchParams();
  const urlTab = params.get('tab');
  const tab = TABS.some((x) => x.key === urlTab) ? urlTab : 'week';
  const setTab = useCallback((key) => {
    const next = new URLSearchParams(params);
    if (key === 'week') next.delete('tab'); else next.set('tab', key);
    // replace, so flicking between tabs does not fill the back button with them
    setParams(next, { replace: true });
  }, [params, setParams]);

  const monday = menuState.week1Monday ? isoToLocal(menuState.week1Monday) : servedMonday();
  const monday2 = menuState.week2Monday ? isoToLocal(menuState.week2Monday) : addDays(monday, 7);
  const weekLabel = `${t('week_of')} ${fmt(monday)}`;
  const week2Label = `${t('week_of')} ${fmt(monday2)}`;
  // Between Thursday's rotation and Monday, "this week's" meals are the ones
  // arriving, not the ones already in the fridge.
  const week1Upcoming = monday > startOfToday();
  const weekNote = week1Upcoming ? `${t('week_info_note_upcoming')} ${fmt(monday)}.` : t('week_info_note');
  const week2Note = menuState.open ? t('week2_order_note') : t('week2_closed_note');

  const week1 = useMemo(() => dishes.filter((d) => d.availability === 'week1'), [dishes]);
  const week2 = useMemo(() => dishes.filter((d) => d.availability === 'week2'), [dishes]);

  let list, subtitle;
  if (tab === 'week') { list = week1; subtitle = weekLabel; }
  else if (tab === 'week2') { list = week2; subtitle = week2Label; }
  else { list = dishes.filter((d) => d.category === tab); subtitle = null; }

  const active = TABS.find((x) => x.key === tab);
  const Icon = active.icon;

  return (
    <div className="min-h-screen bg-[#faf9f5]">
      <Header />
      <Hero
        weekDishes={tab === 'week2' ? week2 : week1}
        weekLabel={tab === 'week2' ? week2Label : weekLabel}
        titleKey={tab === 'week2' ? 'hero_title_week2' : 'hero_title'}
      />
      <CategoryFilter tabs={TABS} active={tab} onChange={setTab} />

      <main className="max-w-[1280px] mx-auto px-5 py-12">
        {/* A basket survives in this browser between visits, so say so rather
            than letting someone forget they were halfway through an order. */}
        {cartNotice && (
          <div data-testid="cart-pruned-notice" className="mb-6 flex items-start justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
            <div className="text-sm text-amber-900">
              <p className="font-medium">{t('cart_pruned_title')}</p>
              {cartNotice.movedToFridge?.length > 0 && (
                <p className="text-xs mt-1">
                  {cartNotice.movedToFridge.map((i) => tf(i.name)).join(', ')} — {t('cart_pruned_moved')}
                </p>
              )}
              {cartNotice.noLongerListed?.length > 0 && (
                <p className="text-xs mt-1">
                  {cartNotice.noLongerListed.length} {t('cart_pruned_gone')}
                </p>
              )}
            </div>
            <button onClick={dismissCartNotice} aria-label={t('close')} className="shrink-0 text-amber-700 hover:text-amber-900">
              <CloseIcon size={16} />
            </button>
          </div>
        )}

        {cartCount > 0 && (
          <button
            onClick={() => setCartOpen(true)}
            data-testid="basket-reminder"
            className="mb-6 w-full flex items-center justify-between gap-3 rounded-xl border border-[#d8dcc6] bg-[#f6f4ec] px-4 py-3 text-left hover:bg-[#eef0e6] transition-colors"
          >
            <span className="flex items-center gap-2.5 min-w-0">
              <ShoppingBag size={18} className="text-[#4b5e34] shrink-0" />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-[#2f3826]">{t('basket_waiting')}</span>
                <span className="block text-xs text-neutral-600">{cartCount} {t('meals_in_basket')}</span>
              </span>
            </span>
            <span className="flex items-center gap-2 shrink-0">
              <PreorderCountdown compact />
              <span className="hidden sm:inline text-sm font-medium text-[#4b5e34]">{t('finish_order')} →</span>
            </span>
          </button>
        )}

        <div className="flex items-center gap-3 mb-8">
          <span className="w-1.5 h-9 rounded-full bg-[#4b5e34]" />
          <div>
            <h2 data-testid="menu-section-title" className="font-display text-[28px] leading-none text-[#2f3826] flex items-center gap-2 flex-wrap">
              <Icon size={22} className="text-[#b8863b]" /> {t(active.labelKey)}
              {tab === 'week2' && (
                <span data-testid="preorder-badge" className="text-[11px] font-semibold uppercase tracking-wider text-[#8a6224] bg-[#f6efe2] border border-[#e7d9bd] px-2 py-1 rounded-full">
                  {t('preorder_badge')}
                </span>
              )}
            </h2>
            {subtitle && <p data-testid="menu-section-subtitle" className="text-sm text-neutral-500 mt-1">{subtitle}</p>}
            {tab === 'week' && <p data-testid="week-info-note" className="text-xs text-[#4b5e34] mt-1">{weekNote}</p>}
            {tab === 'week2' && (
              <div className="flex flex-wrap items-center gap-2 mt-1.5">
                <p data-testid="week2-order-note" className={`text-xs ${menuState.open ? 'text-[#4b5e34]' : 'text-amber-800'}`}>{week2Note}</p>
                <PreorderCountdown compact />
              </div>
            )}
            {['entrees', 'desserts', 'boissons'].includes(tab) && <p className="text-xs text-[#4b5e34] mt-1">{t('always_info_note')}</p>}
          </div>
        </div>

        {list.length === 0 ? (
          <div data-testid="menu-empty-state" className="text-center py-24 text-neutral-500">
            <UtensilsCrossed size={40} className="mx-auto mb-4 text-neutral-300" />
            {t('no_items')}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {list.map((d) => <DishCard key={d.id} dish={d} />)}
          </div>
        )}
      </main>
      <AssistantWidget />
      <Footer />
    </div>
  );
}
